import React, { useReducer, useEffect, useState } from 'react'
import { Box, Checkbox, FormControlLabel, styled } from '@mui/material'
import { Icon } from 'packages/eid-icons'
import { useTranslation } from 'react-i18next'
import DateTimeFilter from '../DateTimeFilter'
import Button from '../Button'
import { isNilOrEmpty, useGetControlsAccess } from 'packages/core'
import { Spinner, Tooltip } from 'packages/eid-ui'
import moment from 'moment'
import { useEditDelegation, useJustificationOptions } from 'hooks'
import { styles, ItemContainer, reducers } from './utils'
import {
    mobileScreenWidth,
    ITEM_COMMENT_MAX_LENGTH,
    ITEM_COMMENT_MIN_LENGTH,
} from 'utils'
import { JustificationSelector } from 'packages/eid-ui/JustificationSelector'
import useSubcomponents from 'useSubcomponents'

const StyledFormControlLabel = styled(FormControlLabel)(({ checked }) => ({
    '& .MuiFormControlLabel-label': {
        color: checked ? '#000000' : '#767676',
    },
}))

const StyledDateFilterContainer = styled(Box)({
    [`@media (max-width:${mobileScreenWidth})`]: {
        width: '100%',
    },
})

const LabelBox = styled(Box)({
    color: '#73767A',
    fontSize: '1.2rem',
    paddingLeft: '0.8rem',
    marginBottom: '-0.4rem',
})

const EditDelegationForm = ({ onSave, onCancel, delegatee, attributeData }) => {
    const [state, dispatch] = useReducer(reducers, {
        startDate: isNilOrEmpty(delegatee.startDateUtc)
            ? null
            : moment.utc(delegatee.startDateUtc),
        endDate: isNilOrEmpty(delegatee.endDateUtc)
            ? null
            : moment.utc(delegatee.endDateUtc),
        delegateForever:
            !delegatee.activeOnlyIfOutOfOffice &&
            isNilOrEmpty(delegatee.endDateUtc)
                ? true
                : false,
        activeOnlyIfOutOfOffice: delegatee.activeOnlyIfOutOfOffice,
    })

    const [justification, setJustification] = useState({
        comment: '',
        isPredefined: false,
        localKeyName: null,
    })

    const { editUrl, revalidateUrl, appendData } = attributeData.component

    const { t } = useTranslation()

    const controls = useGetControlsAccess().map((c) => c.name)
    const { hasAccessToControl } = useSubcomponents()

    const hasJustificationAccess = hasAccessToControl(
        'MyIdentity-PermanentDelegations-Justification-Control',
    )

    const hasOutOfOfficeAccess = controls.includes(
        'MyIdentity-PermanentDelegationsOutOfOfficePermanentDelegation-Control',
    )

    const {
        data: justificationOptions = [],
        isLoading: isLoadingJustifications,
    } = useJustificationOptions()

    // Initialize justification from delegatee data when justificationOptions are loaded
    useEffect(() => {
        if (!isLoadingJustifications && justificationOptions.length > 0) {
            const commentToCheck = delegatee.justificationComment

            if (!isNilOrEmpty(commentToCheck)) {
                // Check if the comment matches a predefined option by localKeyName
                const matchedOption = justificationOptions.find(
                    (opt) => opt.localKeyName === commentToCheck,
                )

                if (matchedOption) {
                    // It's a predefined option - use localKeyName
                    setJustification({
                        comment: '',
                        isPredefined: true,
                        localKeyName: matchedOption.localKeyName,
                    })
                } else {
                    // It's a custom comment (Other option)
                    setJustification({
                        comment: commentToCheck,
                        isPredefined: false,
                        localKeyName: null,
                    })
                }
            }
        }
    }, [
        delegatee.justificationComment,
        justificationOptions,
        isLoadingJustifications,
    ])

    const {
        mutateAsync: editDelegation,
        isLoading,
        isSuccess,
    } = useEditDelegation(editUrl, revalidateUrl, appendData)
    const handleEdit = () => {
        const dataToSubmit = {
            id: delegatee.id,
            delegatorPersonID: delegatee.delegatorPersonID,
            delegateePersonId: delegatee.delegateePersonId,
            delegateeFriendlyName: delegatee.delegateeFriendlyName,
            approvalFlowStepId: delegatee.approvalFlowStepId,
            approvalFlowStepFriendlyName:
                delegatee.approvalFlowStepFriendlyName,

            startDateUtc: state.startDate,
            endDateUtc: state.endDate,
        }
        if (hasOutOfOfficeAccess) {
            dataToSubmit.activeOnlyIfOutOfOffice = state.activeOnlyIfOutOfOffice
        }
        if (hasJustificationAccess) {
            if (justification.isPredefined && justification.localKeyName) {
                // Send localKeyName for predefined options (for localization)
                dataToSubmit.justificationComment = justification.localKeyName
            } else if (justification.comment) {
                // Send custom comment for "Other" option
                dataToSubmit.justificationComment = justification.comment
            }
        }

        editDelegation(dataToSubmit)
    }

    useEffect(() => {
        if (isSuccess) onSave()
    }, [isSuccess, onSave])

    const endDateCheck =
        (!state.delegateForever && isNilOrEmpty(state.endDate)) ||
        state.startDate?.isAfter(state.endDate)

    const isJustificationValid = () => {
        if (!hasJustificationAccess) {
            return true // No validation if user doesn't have access
        }

        // If a predefined option is selected, it's always valid
        if (justification.isPredefined) {
            return true
        }

        // For "Other" option, validate the custom text
        const commentLength = justification.comment?.length || 0

        // Check min length
        if (commentLength < ITEM_COMMENT_MIN_LENGTH) {
            return false
        }

        // Check max length
        if (commentLength > ITEM_COMMENT_MAX_LENGTH) {
            return false
        }

        return true
    }

    return (
        <>
            <ItemContainer display="flex" marginLeft="12px">
                <StyledFormControlLabel
                    sx={{
                        marginRight: '30px',
                    }}
                    control={
                        <Checkbox
                            checked={state.delegateForever}
                            icon={<Icon name="CheckBox" />}
                            checkedIcon={<Icon name="CheckedBox" />}
                            onChange={(e) =>
                                dispatch({
                                    type: 'SET_DELEGATE_FOREVER',
                                    payload: e.target.checked,
                                })
                            }
                        />
                    }
                    checked={state.delegateForever}
                    label={t('DelegateForever')}
                />
                {hasOutOfOfficeAccess && (
                    <StyledFormControlLabel
                        data-protectedsubcomponent={
                            'MyIdentity-PermanentDelegationsOutOfOfficePermanentDelegation-Control'
                        }
                        control={
                            <Checkbox
                                checked={state.activeOnlyIfOutOfOffice}
                                icon={<Icon name="CheckBox" />}
                                checkedIcon={<Icon name="CheckedBox" />}
                                onChange={(e) =>
                                    dispatch({
                                        type: 'SET_OUT_OF_OFFICE',
                                        payload: e.target.checked,
                                    })
                                }
                            />
                        }
                        checked={state.activeOnlyIfOutOfOffice}
                        label={t('OnlyWhenOutOfOffice')}
                    />
                )}
            </ItemContainer>

            <ItemContainer display="flex" marginLeft="-8px">
                <StyledDateFilterContainer padding="0px 8px">
                    <DateTimeFilter
                        disablePast={true}
                        label={t('StartDate')}
                        placeholder={t('SelectDate')}
                        onChange={(date) => {
                            let dateToSet
                            if (!isNilOrEmpty(date)) {
                                dateToSet = moment(date)
                                    .clone()
                                    .startOf('day')
                                    .utc()
                            }
                            dispatch({
                                type: 'SET_START_DATE',
                                payload: dateToSet,
                            })
                        }}
                        value={
                            isNilOrEmpty(state.startDate)
                                ? null
                                : state.startDate.clone().local()
                        }
                    />
                </StyledDateFilterContainer>

                {!state.delegateForever && (
                    <StyledDateFilterContainer padding="0px 8px ">
                        <DateTimeFilter
                            minDate={
                                !isNilOrEmpty(state.startDate) &&
                                state.startDate.isAfter(moment().startOf('day'))
                                    ? state.startDate.clone().local()
                                    : moment().startOf('day')
                            }
                            label={t('EndDate')}
                            placeholder={t('SelectDate')}
                            onChange={(date) => {
                                let dateToSet
                                if (!isNilOrEmpty(date)) {
                                    dateToSet = moment(date)
                                        .clone()
                                        .endOf('day')
                                        .subtract(1, 'minutes')
                                        .utc()
                                }
                                dispatch({
                                    type: 'SET_END_DATE',
                                    payload: dateToSet,
                                })
                            }}
                            value={
                                isNilOrEmpty(state.endDate)
                                    ? null
                                    : state.endDate.clone().local()
                            }
                        />
                    </StyledDateFilterContainer>
                )}
            </ItemContainer>

            {hasJustificationAccess && (
                <ItemContainer
                    data-protectedsubcomponent={
                        'MyIdentity-PermanentDelegations-Justification-Control'
                    }
                >
                    {isLoadingJustifications ? (
                        <>
                            <Spinner size={0.8} />
                        </>
                    ) : (
                        <>
                            <LabelBox>{t('Justification')}</LabelBox>
                            <JustificationSelector
                                options={justificationOptions}
                                value={
                                    justification.isPredefined
                                        ? justification.localKeyName
                                        : justification.comment
                                }
                                onChange={(comment, isPredefined) => {
                                    // For predefined: comment is the localKeyName
                                    // For custom: comment is the actual text
                                    setJustification({
                                        comment: isPredefined ? '' : comment, // Store custom text
                                        isPredefined: isPredefined || false,
                                        localKeyName: isPredefined
                                            ? comment
                                            : null, // Store localKeyName
                                    })
                                }}
                                multiline={true}
                                maxCharacters={ITEM_COMMENT_MAX_LENGTH}
                                minCharacters={ITEM_COMMENT_MIN_LENGTH}
                                showCharacterCount={true}
                                isLoading={isLoadingJustifications}
                            />
                        </>
                    )}
                </ItemContainer>
            )}

            <ItemContainer
                display="flex"
                marginLeft="-8px"
                flexDirection="row !important"
                flexWrap="wrap"
            >
                <Tooltip title={endDateCheck ? t('RequiredFieldsMissing') : ''}>
                    <Box>
                        <Button.Rounded
                            styles={{ minWidth: '144px !important' }}
                            loading={isLoading}
                            disabled={
                                isNilOrEmpty(state.startDate) ||
                                endDateCheck ||
                                isLoading ||
                                !isJustificationValid()
                            }
                            onClick={() => {
                                handleEdit()
                            }}
                        >
                            {t('SaveChanges')}
                        </Button.Rounded>
                    </Box>
                </Tooltip>

                <Button.Rounded onClick={onCancel} styles={styles.cancelButton}>
                    {t('Cancel')}
                </Button.Rounded>
            </ItemContainer>
        </>
    )
}

export default EditDelegationForm
